import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Container, SimpleGrid, Paper, ThemeIcon, Textarea, Group, Stack, Badge,
  TextInput, Button, Radio, FileInput, NumberInput, Select, Divider, Box, rem, Loader, Center, Modal
} from '@mantine/core';
import {
  IconClipboardText, IconUpload, IconCheck, IconArrowLeft,
  IconFileText, IconCalendarEvent, IconBuilding, IconBuildingSkyscraper, IconReceipt2, IconFileExcel, IconDownload
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

const heroGradient = 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(37,99,235,0.88) 100%)';

export default function ConcreteTracking() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // İçe Aktarma Stateleri
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // API'den gelecek veriler
  const [settings, setSettings] = useState({ concretes: [], sites: [], companies: [] });

  const [deliveryType, setDeliveryType] = useState('ic_santiye');
  const [formData, setFormData] = useState({
    waybillNo: '', concreteTypeId: '', volume: '', date: '',
    description: '', siteId: '', companyId: '', invoiceNo: '', invoiceDate: '',
  });

  const [files, setFiles] = useState({ waybillPdf: null, invoicePdf: null });

  // Ayarları Yükle
  useEffect(() => {
    api.get('/records/settings-data')
      .then(res => {
        setSettings(res.data);
        setLoading(false);
      })
      .catch(() => notifications.show({ title: 'Hata', message: 'Ayarlar yüklenemedi', color: 'red' }));
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Butona Tıklandığında Çalışan Kayıt Fonksiyonu
  const handleSubmit = async () => {
    // Frontend Doğrulamaları
    if (!formData.waybillNo || !formData.date || !formData.concreteTypeId || !formData.volume) {
      notifications.show({ title: 'Eksik Bilgi', message: 'Lütfen irsaliye no, tarih, beton cinsi ve hacim bilgilerini doldurun.', color: 'orange' });
      return;
    }
    if (deliveryType === 'ic_santiye' && !formData.siteId) {
      notifications.show({ title: 'Eksik Bilgi', message: 'Lütfen teslim edilecek şantiyeyi seçin.', color: 'orange' });
      return;
    }
    if (deliveryType === 'dis_satis' && !formData.companyId) {
      notifications.show({ title: 'Eksik Bilgi', message: 'Lütfen fatura kesilecek firmayı seçin.', color: 'orange' });
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    
    data.append('waybillNo', formData.waybillNo);
    data.append('date', formData.date);
    data.append('concreteTypeId', formData.concreteTypeId);
    data.append('volume', formData.volume);
    data.append('deliveryType', deliveryType);
    if (formData.description) data.append('description', formData.description);

    if (deliveryType === 'ic_santiye') {
      data.append('siteId', formData.siteId);
    } else {
      data.append('companyId', formData.companyId);
    }

    if (formData.invoiceNo) data.append('invoiceNo', formData.invoiceNo);
    if (formData.invoiceDate) data.append('invoiceDate', formData.invoiceDate);

    if (files.waybillPdf) data.append('waybillPdf', files.waybillPdf);
    if (files.invoicePdf) data.append('invoicePdf', files.invoicePdf);

    try {
      await api.post('/records/concrete', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      notifications.show({ 
        title: 'Başarılı', 
        message: 'Kayıt sisteme eksiksiz bir şekilde işlendi. Sayfa yenileniyor...', 
        color: 'teal',
        icon: <IconCheck size={16} />
      });

      // Kayıt başarılı olduktan sonra 1.5 saniye gecikmeli sayfa yenileme
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Kayıt oluşturulamadı';
      notifications.show({ title: 'Hata', message: typeof errMsg === 'string' ? errMsg : 'Kayıt başarısız.', color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  // Toplu Excel İçe Aktarma İşlemi
  const handleImportExcel = async () => {
    if (!importFile) {
      notifications.show({ title: 'Uyarı', message: 'Lütfen bir Excel dosyası seçin.', color: 'orange' });
      return;
    }

    setImporting(true);
    const importData = new FormData();
    importData.append('file', importFile);

    try {
      const res = await api.post('/records/concrete/import', importData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      notifications.show({ 
        title: 'Başarılı', 
        message: res.data.message || 'Excel başarıyla içe aktarıldı. Sayfa yenileniyor...', 
        color: 'teal',
        icon: <IconCheck size={16} />
      });
      
      setImportModalOpen(false);
      setImportFile(null);

      // Başarılı excel yüklemesi sonrası 1.5 saniye gecikmeli sayfa yenileme
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (err) {
      const msg = err.response?.data?.detail || 'İçe aktarma sırasında bir hata oluştu.';
      notifications.show({ title: 'Hata', message: typeof msg === 'string' ? msg : 'Hata', color: 'red' });
    } finally {
      setImporting(false);
    }
  };

  // Excel Şablonu İndirme İşlemi
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await api.get('/records/concrete/export-template', {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Beton_Irsaliye_Sablonu.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notifications.show({ title: 'Hata', message: 'Şablon indirilemedi.', color: 'red' });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  if (loading) return <Center h="100vh"><Loader size="xl" /></Center>;

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>

      {/* EXCEL İÇE AKTARMA MODALI */}
      <Modal 
        opened={importModalOpen} 
        onClose={() => setImportModalOpen(false)} 
        title={<Text fw={700}>Excel'den Toplu Veri Yükle</Text>} 
        radius="xl" 
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            İşlemi hızlandırmak için örnek şablonu indirebilirsiniz. Dosyanızdaki sütun başlıkları şablonla aynı olmalıdır.
          </Text>
          
          {/* Şablon İndirme Butonu */}
          <Button 
            variant="outline" 
            color="gray" 
            radius="md" 
            size="sm" 
            leftSection={<IconDownload size={16} />}
            loading={downloadingTemplate}
            onClick={handleDownloadTemplate}
          >
            Örnek Şablonu İndir
          </Button>
          
          <Divider my="xs" />

          <FileInput
            label="Excel Dosyası"
            placeholder="Doldurduğunuz dosyayı seçin"
            accept=".xlsx, .xls"
            leftSection={<IconFileExcel size={16} />}
            value={importFile}
            onChange={setImportFile}
            radius="md"
            clearable
          />
          
          <Button 
            radius="xl" 
            color="blue" 
            onClick={handleImportExcel} 
            loading={importing} 
            fullWidth
            mt="sm"
          >
            Verileri İçe Aktar
          </Button>
        </Stack>
      </Modal>

      <Stack gap="xl">
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            background: heroGradient,
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
          }}
        >
          <Box style={{ position: 'absolute', top: -80, right: -80, width: 240, height: 240, background: 'rgba(59,130,246,0.25)', filter: 'blur(120px)', borderRadius: '50%', zIndex: 0 }} />

          <Stack gap="md" style={{ position: 'relative', zIndex: 2 }}>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Button
                variant="white" radius="xl" size="sm" leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate('/beton-takip')}
                style={{ background: 'rgba(255,255,255,0.12)', color: 'white', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.15)' }}
              >
                Geri Dön
              </Button>
              <Group gap="sm">
                <Button
                  variant="light" color="white" radius="xl" size="sm"
                  leftSection={<IconFileExcel size={16} />}
                  onClick={() => setImportModalOpen(true)}
                  style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  Toplu İçe Aktar
                </Button>
                <Badge variant="light" color="cyan" radius="xl" size="lg">Yeni Kayıt</Badge>
              </Group>
            </Group>

            <Group gap="sm" align="center">
              <ThemeIcon size={44} radius="xl" variant="white" color="blue">
                <IconClipboardText size={22} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text size="sm" fw={600} c="rgba(255,255,255,0.75)">Beton Yönetim Sistemi</Text>
                <Title order={1} fw={900} c="white">Beton İrsaliye Kaydı</Title>
              </Stack>
            </Group>

            <Group justify="space-between" align="flex-end" wrap="wrap">
              <Text c="rgba(255,255,255,0.78)" size="sm" maw={700}>
                İrsaliye ve fatura bilgilerini dijital arşive işleyin. Tüm süreç tek panelden,
                hatasız ve izlenebilir şekilde yönetilir.
              </Text>
            </Group>
          </Stack>
        </Paper>

        <Paper radius="xl" p="xl" withBorder>
          <Stack gap="lg">
            <Title order={4}>Genel İrsaliye Bilgileri</Title>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <TextInput label="İrsaliye No" leftSection={<IconFileText size={16} />} value={formData.waybillNo} onChange={(e) => handleChange('waybillNo', e.target.value)} required radius="md" />
              <TextInput type="date" label="Tarih" leftSection={<IconCalendarEvent size={16} />} value={formData.date} onChange={(e) => handleChange('date', e.target.value)} required radius="md" />
              
              <Select label="Beton Cinsi" placeholder="Seçiniz" 
                data={settings.concretes.map(c => ({ value: String(c.id), label: c.name }))}
                value={formData.concreteTypeId} onChange={(val) => handleChange('concreteTypeId', val)} required radius="md" />
              
              <NumberInput label="Metreküp (m³)" min={0} decimalScale={2} value={formData.volume} onChange={(val) => handleChange('volume', val)} required radius="md" />
            </SimpleGrid>

            <Textarea label="Açıklama" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} radius="md" />
            <FileInput label="İrsaliye PDF" accept="application/pdf" leftSection={<IconUpload size={16} />} 
              value={files.waybillPdf} onChange={(file) => setFiles(prev => ({...prev, waybillPdf: file}))} radius="md" clearable />

            <Divider my="sm" variant="dashed" />

            <Title order={4}>Teslimat Detayları</Title>
            <Radio.Group value={deliveryType} onChange={setDeliveryType} required>
              <Group mt="xs"><Radio value="ic_santiye" label="İç Şantiye" /><Radio value="dis_satis" label="Dış Satış" color="pink" /></Group>
            </Radio.Group>

            <Box p="md" style={{ background: 'rgba(241,245,249,0.5)', borderRadius: rem(12), border: '1px solid #e2e8f0' }}>
              {deliveryType === 'ic_santiye' ? (
                <Stack gap="md">
                  <Select label="Teslim Edilen Şantiye" placeholder="Seçiniz" leftSection={<IconBuilding size={16} />}
                    data={settings.sites.map(s => ({ value: String(s.id), label: s.name }))}
                    value={formData.siteId} onChange={(val) => handleChange('siteId', val)} searchable required radius="md" />
                  <SimpleGrid cols={{ base: 1, md: 2 }}>
                    <TextInput label="Fatura No" leftSection={<IconReceipt2 size={16} />} value={formData.invoiceNo} onChange={(e) => handleChange('invoiceNo', e.target.value)} radius="md" />
                    <TextInput type="date" label="Fatura Tarihi" leftSection={<IconCalendarEvent size={16} />} value={formData.invoiceDate} onChange={(e) => handleChange('invoiceDate', e.target.value)} radius="md" />
                  </SimpleGrid>
                  <FileInput label="Fatura PDF" accept="application/pdf" leftSection={<IconUpload size={16} />} value={files.invoicePdf} onChange={(file) => setFiles(prev => ({...prev, invoicePdf: file}))} radius="md" clearable />
                </Stack>
              ) : (
                <Stack gap="md">
                  <Select label="Firma Ünvanı" placeholder="Seçiniz" leftSection={<IconBuildingSkyscraper size={16} />}
                    data={settings.companies.map(c => ({ value: String(c.id), label: c.name }))}
                    value={formData.companyId} onChange={(val) => handleChange('companyId', val)} searchable required radius="md" />
                  <SimpleGrid cols={{ base: 1, md: 2 }}>
                    <TextInput label="Fatura No" leftSection={<IconReceipt2 size={16} />} value={formData.invoiceNo} onChange={(e) => handleChange('invoiceNo', e.target.value)} radius="md" />
                    <TextInput type="date" label="Fatura Tarihi" leftSection={<IconCalendarEvent size={16} />} value={formData.invoiceDate} onChange={(e) => handleChange('invoiceDate', e.target.value)} radius="md" />
                  </SimpleGrid>
                  <FileInput label="Fatura PDF" accept="application/pdf" leftSection={<IconUpload size={16} />} value={files.invoicePdf} onChange={(file) => setFiles(prev => ({...prev, invoicePdf: file}))} radius="md" clearable />
                </Stack>
              )}
            </Box>

            <Group justify="flex-end" mt="md">
              <Button variant="light" color="gray" radius="xl" onClick={() => navigate('/beton-takip')}>İptal</Button>
              <Button leftSection={<IconCheck size={18} />} radius="xl" color="blue" loading={submitting} onClick={handleSubmit}>Kaydı Tamamla</Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}